import React from 'react';
import { isKingRole, getVoteWeight, hasActiveVoters, groupSuggestionsByMove } from './utils/gameUtils';

// Suggestions list + active poll voting UI
const VotingPanel = ({
  game,
  playerTeam,
  playerData,
  currentUserEmail,
  selectedMoves,
  setSelectedMoves,
  createPoll,
  voteOnMove,
  executeWinningMove,
  setPreviewedMove,
}) => {
  const hasKingRoleCheck = isKingRole(playerData?.role);
  const activePoll = playerTeam === 'white' ? game.activePolls?.white : game.activePolls?.black;
  const suggestions = playerTeam === 'white' ? game.suggestedMoves?.white : game.suggestedMoves?.black;
  const myVote = activePoll?.votes?.[currentUserEmail];
  const teamPlayers = playerTeam === 'white' ? game.whiteTeam : game.blackTeam;

  // Active poll - show voting UI
  if (activePoll && suggestions) {
    const pollMoves = suggestions.filter(s => activePoll.moves.includes(s.id));
    const uniquePollMoves = groupSuggestionsByMove(pollMoves);

    // Calculate weighted votes
    const voteCounts = {};
    Object.entries(activePoll.votes || {}).forEach(([voterEmail, moveId]) => {
      const voter = teamPlayers?.find(p => p.email === voterEmail);
      if (voter) {
        const weight = getVoteWeight(voter.role);
        voteCounts[moveId] = (voteCounts[moveId] || 0) + weight;
      }
    });

    // Check for ties
    const moveVoteTotals = uniquePollMoves.map(move => {
      const totalVotes = move.ids.reduce((sum, id) => sum + (voteCounts[id] || 0), 0);
      return { moveId: move.ids[0], votes: totalVotes };
    });
    const maxVotes = Math.max(...moveVoteTotals.map(m => m.votes), 0);
    const tiedMoves = moveVoteTotals.filter(m => m.votes === maxVotes && m.votes > 0);
    const hasTie = tiedMoves.length > 1;

    return (
      <div className="suggestions-sidebar">
        <div className="poll-container">
          <h3 className="poll-title">🗳️ ACTIVE POLL</h3>
          <p className="poll-subtitle">{hasKingRoleCheck ? 'Watch the votes come in!' : 'Vote for a Move!'}</p>
          <p className="poll-creator">By {activePoll.createdByName}</p>
          {hasKingRoleCheck && hasTie && (
            <div className="tie-warning">
              ⚠️ TIE! Click on a move to execute it
            </div>
          )}

          <div className="poll-moves-sidebar">
            {uniquePollMoves.map((move) => {
              const totalVotes = move.ids.reduce((sum, id) => sum + (voteCounts[id] || 0), 0);
              const isMyVote = move.ids.includes(myVote);
              const isTied = hasTie && tiedMoves.some(tm => tm.moveId === move.ids[0]);

              return (
                <div
                  key={move.ids[0]}
                  className={`poll-move ${isMyVote ? 'voted' : ''} ${isTied ? 'tied' : ''}`}
                  onClick={() => {
                    setPreviewedMove({ from: move.from, to: move.to });
                    if (hasKingRoleCheck && hasTie) {
                      executeWinningMove(move.ids[0]);
                    } else {
                      voteOnMove(move.ids[0]);
                    }
                  }}
                  onMouseEnter={() => setPreviewedMove({ from: move.from, to: move.to })}
                  onMouseLeave={() => setPreviewedMove(null)}
                >
                  <div className="poll-move-text">
                    {move.displayText}
                    {move.count > 1 && <span className="suggestion-count-badge">×{move.count}</span>}
                  </div>
                  <div className="poll-move-info">
                    <span className="poll-move-player">by {move.players.join(', ')}</span>
                    <span className="poll-move-votes">{totalVotes} pts</span>
                  </div>
                  {isMyVote && !hasKingRoleCheck && <div className="my-vote-badge">YOUR VOTE</div>}
                </div>
              );
            })}
          </div>

          {hasKingRoleCheck && !hasTie && (
            <button onClick={() => executeWinningMove()} className="execute-button">
              ⚔️ Execute Move
            </button>
          )}
        </div>
      </div>
    );
  }

  // No active poll - show suggestions
  if (suggestions && suggestions.length > 0) {
    const uniqueMoves = groupSuggestionsByMove(suggestions);

    const toggleMoveSelection = (moveIds) => {
      const moveIdArray = Array.isArray(moveIds) ? moveIds : [moveIds];
      const isAlreadySelected = moveIdArray.some(id => selectedMoves.includes(id));

      if (isAlreadySelected) {
        setSelectedMoves(selectedMoves.filter(id => !moveIdArray.includes(id)));
      } else if (selectedMoves.length < 3) {
        setSelectedMoves([...selectedMoves, moveIdArray[0]]);
      }
    };

    return (
      <div className="suggestions-sidebar">
        <div className="suggestions-container">
          <h3 className="suggestions-title">💡 Moves</h3>
          <p className="suggestions-count">({uniqueMoves.length} unique, {suggestions.length} total)</p>
          {hasKingRoleCheck && (
            <p className="king-instruction">Select up to 3 moves to create a poll</p>
          )}

          <div className="suggestions-list-sidebar">
            {uniqueMoves.map((move) => {
              const isSelected = move.ids.some(id => selectedMoves.includes(id));

              return (
                <div
                  key={move.ids[0]}
                  className={`suggestion-item ${hasKingRoleCheck ? 'selectable' : ''} ${isSelected ? 'selected' : ''}`}
                  onClick={() => hasKingRoleCheck && toggleMoveSelection(move.ids)}
                  onMouseEnter={() => setPreviewedMove({ from: move.from, to: move.to })}
                  onMouseLeave={() => setPreviewedMove(null)}
                >
                  <div className="suggestion-move">
                    {move.displayText}
                    {move.count > 1 && <span className="suggestion-count-badge">×{move.count}</span>}
                  </div>
                  <div className="suggestion-player">
                    by {move.players.join(', ')}
                  </div>
                  {isSelected && <div className="selected-badge">✓</div>}
                </div>
              );
            })}
          </div>

          {hasKingRoleCheck && selectedMoves.length > 0 && (
            <button
              onClick={() => {
                createPoll(selectedMoves);
                setSelectedMoves([]);
              }}
              className="create-poll-button"
            >
              {hasActiveVoters(game, playerTeam)
                ? `📊 Create Poll (${selectedMoves.length})`
                : `⚡ Execute Move`
              }
            </button>
          )}
        </div>
      </div>
    );
  }

  // No suggestions yet
  return (
    <div className="suggestions-sidebar">
      <div className="no-suggestions">
        <p>No move suggestions yet. Drag a piece to suggest a move!</p>
      </div>
    </div>
  );
};

export default VotingPanel;
